import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Link, Route, Routes, useNavigate, useParams, useSearchParams } from "react-router-dom";

const ACCEPTANCE_SEED = `Business: LedgerWing
Offer: AI-assisted invoice reconciliation for recruitment agencies
Audience: UK recruitment agencies with 20 to 200 employees
Region: United Kingdom
Launch goal: book 15 qualified product demonstrations within 30 days
Core problem: finance teams spend too much time matching contractor invoices, timesheets and client billing records
Differentiator: flags mismatches before payroll and client invoicing deadlines
Constraints: use evidence-backed claims, do not invent customer results, avoid spam language, and keep the campaign suitable for professional B2B outreach`;

function apiUrl(path) {
  return path;
}

async function api(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.success === false) {
    throw new Error(payload.details || payload.error || `Request failed (${response.status})`);
  }
  return payload.data;
}

function ownerId() {
  const key = "signallaunch_owner_id";
  const existing = window.localStorage.getItem(key);
  if (existing) return existing;
  const created = `owner-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  window.localStorage.setItem(key, created);
  return created;
}

function AppShell({ children }) {
  return (
    <div className="sl-app">
      <header className="sl-header">
        <Link className="sl-brand" to="/">
          <span className="sl-mark">SL</span>
          <span>
            <strong>SignalLaunch</strong>
            <small>Revenue Campaign Engine</small>
          </span>
        </Link>
        <nav>
          <a href="#campaign-detail">Campaign Detail</a>
          <a href="#history">History</a>
        </nav>
      </header>
      {children}
    </div>
  );
}

function Home() {
  const navigate = useNavigate();
  const [seedText, setSeedText] = useState(ACCEPTANCE_SEED);
  const [campaign, setCampaign] = useState(null);
  const [selectedRank, setSelectedRank] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState("");
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const oid = useMemo(ownerId, []);

  useEffect(() => {
    api(`/api/signallaunch/campaigns/by-owner/${encodeURIComponent(oid)}`)
      .then((data) => setRecentCampaigns(data.campaigns || []))
      .catch(() => {});
  }, [oid]);

  async function createPreview() {
    setLoading(true);
    setError("");
    try {
      const data = await api("/api/signallaunch/preview", {
        method: "POST",
        body: JSON.stringify({ seedText, ownerId: oid }),
      });
      setCampaign(data.campaign);
      setSelectedRank(1);
      navigate(`/campaign/${data.campaign.id}`, { replace: false });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function continueToCheckout() {
    if (!campaign) return;
    setCheckoutLoading(true);
    setError("");
    try {
      const data = await api(`/api/signallaunch/campaigns/${campaign.id}/select-and-checkout`, {
        method: "POST",
        body: JSON.stringify({ directionRank: selectedRank, ownerId: oid }),
      });
      window.location.href = data.checkoutUrl;
    } catch (err) {
      setError(err.message);
      setCheckoutLoading(false);
    }
  }

  return (
    <AppShell>
      <main className="sl-workspace">
        <section className="sl-intake">
          <div className="sl-copy">
            <p className="sl-kicker">Source-backed launch campaign production</p>
            <h1>Paste one offer brief. Choose a direction. Receive the campaign and launch hub automatically.</h1>
            <p>
              Built for B2B founders, small agencies and product-marketing teams that need careful campaign claims, a public output and monitored source signals.
            </p>
          </div>
          <img className="sl-visual" src="/assets/signallaunch-campaign-engine.png" alt="Campaign production workspace" />
        </section>

        <section className="sl-panel sl-seed-panel">
          <div className="sl-panel-head">
            <div>
              <p className="sl-kicker">Minimal seed</p>
              <h2>Offer brief</h2>
            </div>
            <button className="sl-button primary" onClick={createPreview} disabled={loading}>
              {loading ? "Creating preview" : "Create source-backed preview"}
            </button>
          </div>
          <textarea
            value={seedText}
            onChange={(event) => setSeedText(event.target.value)}
            rows={9}
            aria-label="Offer brief"
          />
          {error && <div className="sl-alert">{error}</div>}
        </section>

        {campaign && (
          <PreviewPanel
            campaign={campaign}
            selectedRank={selectedRank}
            setSelectedRank={setSelectedRank}
            continueToCheckout={continueToCheckout}
            checkoutLoading={checkoutLoading}
          />
        )}

        {recentCampaigns.length > 0 && (
          <section className="sl-panel">
            <div className="sl-panel-head">
              <div>
                <p className="sl-kicker">Recoverable work</p>
                <h2>Campaign history</h2>
              </div>
            </div>
            <div className="sl-history-list">
              {recentCampaigns.map((item) => (
                <Link className="sl-history-row" key={item.id} to={`/campaign/${item.id}`}>
                  <strong>{item.originalSeed?.business || "Campaign"}</strong>
                  <span>{item.lifecycleStatus?.replaceAll("_", " ") || "created"}</span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </AppShell>
  );
}

function PreviewPanel({ campaign, selectedRank, setSelectedRank, continueToCheckout, checkoutLoading }) {
  const preview = campaign.engine1Preview;
  return (
    <section className="sl-panel sl-preview" id="campaign-detail">
      <div className="sl-panel-head">
        <div>
          <p className="sl-kicker">Engine 1 preview</p>
          <h2>Choose one campaign direction</h2>
        </div>
        <button className="sl-button primary" onClick={continueToCheckout} disabled={checkoutLoading}>
          {checkoutLoading ? "Opening checkout" : "Save campaign and continue to checkout"}
        </button>
      </div>

      <div className="sl-context">
        <h3>Market context</h3>
        <p>{preview.marketContext.summary}</p>
        <div className="sl-source-counts">
          <span>{preview.sourceCount} preview sources</span>
          {Object.entries(preview.providerCounts || {}).map(([provider, count]) => (
            <span key={provider}>{provider}: {count}</span>
          ))}
        </div>
      </div>

      <div className="sl-directions">
        {preview.directions.map((direction) => (
          <label className={`sl-direction ${selectedRank === direction.rank ? "selected" : ""}`} key={direction.directionId}>
            <input
              type="radio"
              checked={selectedRank === direction.rank}
              onChange={() => setSelectedRank(direction.rank)}
            />
            <span className="sl-rank">Rank {direction.rank}</span>
            <strong>{direction.title}</strong>
            <p>{direction.suggestedPromise}</p>
            <small>{direction.uncertainty}</small>
          </label>
        ))}
      </div>

      <div className="sl-boundaries">
        <h3>Evidence boundaries and uncertainty</h3>
        <ul>
          {preview.marketContext.evidenceBoundaries.map((item) => <li key={item}>{item}</li>)}
          {preview.marketContext.uncertaintyStatements.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </div>
    </section>
  );
}

function CampaignDetail() {
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const [campaign, setCampaign] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const verifyStarted = useRef(false);

  const sessionId = searchParams.get("session_id");
  const checkoutStatus = searchParams.get("checkout");

  const refresh = useCallback(async () => {
    const data = await api(`/api/signallaunch/campaigns/${campaignId}`);
    setCampaign(data.campaign);
    setHistory(data.history || []);
    return data.campaign;
  }, [campaignId]);

  useEffect(() => {
    refresh().catch((err) => setError(err.message));
  }, [campaignId, refresh]);

  useEffect(() => {
    if (checkoutStatus !== "success" || !sessionId || verifyStarted.current) return;
    verifyStarted.current = true;
    setVerifying(true);
    api(`/api/signallaunch/payment/verify?campaignId=${encodeURIComponent(campaignId)}&session_id=${encodeURIComponent(sessionId)}`)
      .then(refresh)
      .catch((err) => setError(err.message))
      .finally(() => setVerifying(false));
  }, [campaignId, checkoutStatus, sessionId, refresh]);

  useEffect(() => {
    const timer = setInterval(() => {
      refresh().catch(() => {});
    }, campaign?.lifecycleStatus === "campaign_ready" ? 15000 : 4000);
    return () => clearInterval(timer);
  }, [campaignId, campaign?.lifecycleStatus, refresh]);

  if (!campaign) {
    return (
      <AppShell>
        <main className="sl-workspace"><div className="sl-panel">Loading campaign...</div></main>
      </AppShell>
    );
  }

  const ready = campaign.lifecycleStatus === "campaign_ready";
  return (
    <AppShell>
      <main className="sl-workspace sl-detail">
        <section className="sl-panel sl-status-panel" id="campaign-detail">
          <div>
            <p className="sl-kicker">Campaign Detail</p>
            <h1>{campaign.originalSeed.business}: {campaign.selectedDirection?.title || "Preview ready"}</h1>
            <p>{campaign.originalSeed.offer}</p>
          </div>
          <div className="sl-status-grid">
            <Status label="Payment" value={campaign.payment?.state || "unpaid"} />
            <Status label="Progress" value={campaign.lifecycleStatus?.replaceAll("_", " ") || "created"} />
            <Status label="Sources" value={`${campaign.sourceAcquisition?.sourceCount || campaign.previewArtifacts?.sourceCount || 0}`} />
            <Status label="Monitoring" value={campaign.monitoring?.status?.replaceAll("_", " ") || "waiting"} />
          </div>
          {verifying && <div className="sl-alert neutral">Verifying checkout and starting full campaign production.</div>}
          {checkoutStatus === "cancelled" && <div className="sl-alert">Checkout was cancelled. Your unpaid campaign is saved and reopenable.</div>}
          {error && <div className="sl-alert">{error}</div>}
          {!campaign.selectedDirection && <PreviewFromDetail campaign={campaign} />}
          {campaign.selectedDirection && !ready && (
            <div className="sl-production-note">Campaign in production. You can close this browser; the completed result will be recoverable here.</div>
          )}
          {ready && campaign.publicOutput?.publicUrl && (
            <a className="sl-button primary" href={campaign.publicOutput.publicUrl}>Open public launch hub</a>
          )}
        </section>

        <section className="sl-panel">
          <div className="sl-panel-head">
            <div>
              <p className="sl-kicker">Lifecycle progress</p>
              <h2>Production stages</h2>
            </div>
          </div>
          <div className="sl-progress-list">
            {(campaign.execution?.progress || []).map((item) => (
              <div className={`sl-progress ${item.state}`} key={item.key}>
                <span>{item.state}</span>
                <strong>{item.label}</strong>
              </div>
            ))}
          </div>
        </section>

        {campaign.sourceAcquisition && <SourceSummary source={campaign.sourceAcquisition} />}
        {campaign.engine2Package && <Engine2Output pkg={campaign.engine2Package} />}
        {campaign.monitoring && <Monitoring monitoring={campaign.monitoring} />}
        <History history={history} />
      </main>
    </AppShell>
  );
}

function PreviewFromDetail({ campaign }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function checkout() {
    setLoading(true);
    setError("");
    try {
      const data = await api(`/api/signallaunch/campaigns/${campaign.id}/select-and-checkout`, {
        method: "POST",
        body: JSON.stringify({ directionRank: 1, ownerId: ownerId() }),
      });
      window.location.href = data.checkoutUrl;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  return (
    <div className="sl-inline-preview">
      <PreviewPanel
        campaign={campaign}
        selectedRank={1}
        setSelectedRank={() => {}}
        continueToCheckout={checkout}
        checkoutLoading={loading}
      />
      {error && <div className="sl-alert">{error}</div>}
      <button className="sl-button secondary" onClick={() => navigate("/")}>Create another preview</button>
    </div>
  );
}

function Status({ label, value }) {
  return (
    <div className="sl-status-card">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SourceSummary({ source }) {
  return (
    <section className="sl-panel">
      <p className="sl-kicker">Source acquisition</p>
      <h2>Post-payment source summary</h2>
      <div className="sl-source-counts">
        <span>{source.sourceCount} source records</span>
        {Object.entries(source.providerCounts || {}).map(([provider, count]) => (
          <span key={provider}>{provider}: {count}</span>
        ))}
      </div>
      <p>Acquired at {source.acquiredAt}. Raw and normalized artifacts are retained with dependency hashes for this campaign.</p>
    </section>
  );
}

function Engine2Output({ pkg }) {
  return (
    <section className="sl-panel">
      <p className="sl-kicker">Campaign package</p>
      <h2>{pkg.selectedPositioning.name}</h2>
      <p>{pkg.selectedPositioning.statement}</p>

      <div className="sl-two">
        <div>
          <h3>Landing-page copy</h3>
          <h4>{pkg.landingPageCopy.heroHeadline}</h4>
          <p>{pkg.landingPageCopy.heroSubhead}</p>
        </div>
        <div>
          <h3>Claim boundaries</h3>
          <ul>{pkg.claimBoundaries.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      </div>

      <h3>Seven-day content sequence</h3>
      <div className="sl-sequence">
        {pkg.sevenDayContentSequence.map((item) => (
          <article key={item.day}>
            <span>Day {item.day}</span>
            <strong>{item.channel}</strong>
            <p>{item.copy}</p>
          </article>
        ))}
      </div>

      <h3>Professional outreach messages</h3>
      <div className="sl-messages">
        {pkg.outreachMessages.map((message) => (
          <article key={message.subject}>
            <strong>{message.subject}</strong>
            <p>{message.body}</p>
          </article>
        ))}
      </div>

      <h3>Next-action plan</h3>
      <ol>{pkg.nextActionPlan.map((item) => <li key={item}>{item}</li>)}</ol>
    </section>
  );
}

function Monitoring({ monitoring }) {
  return (
    <section className="sl-panel">
      <p className="sl-kicker">Signal monitoring</p>
      <h2>{monitoring.status?.replaceAll("_", " ")}</h2>
      <p>{monitoring.recommendation}</p>
      <div className="sl-sequence compact">
        {(monitoring.signals || []).map((signal) => (
          <article key={signal.title}>
            <strong>{signal.title}</strong>
            <p>{signal.interpretation}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function History({ history }) {
  return (
    <section className="sl-panel" id="history">
      <p className="sl-kicker">History</p>
      <h2>Campaign history</h2>
      <div className="sl-timeline">
        {history.map((item) => (
          <div key={item.id}>
            <span>{new Date(item.createdAtIso).toLocaleString()}</span>
            <strong>{item.summary}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}

function PublicHub() {
  const { slug } = useParams();
  const [hub, setHub] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api(`/api/signallaunch/public-hubs/${slug}`)
      .then((data) => setHub(data.hub))
      .catch((err) => setError(err.message));
  }, [slug]);

  if (error) return <AppShell><main className="sl-workspace"><div className="sl-alert">{error}</div></main></AppShell>;
  if (!hub) return <AppShell><main className="sl-workspace"><div className="sl-panel">Loading launch hub...</div></main></AppShell>;

  return (
    <AppShell>
      <main className="sl-public">
        <section className="sl-public-hero">
          <p className="sl-kicker">Public launch hub</p>
          <h1>{hub.landingPageCopy.heroHeadline}</h1>
          <p>{hub.landingPageCopy.heroSubhead}</p>
          <a className="sl-button primary" href={`mailto:?subject=${encodeURIComponent(hub.title)}&body=${encodeURIComponent(hub.publicUrl)}`}>
            Share launch hub
          </a>
        </section>

        <section className="sl-panel">
          <h2>{hub.title}</h2>
          <p>{hub.campaignPromise}</p>
          <div className="sl-two">
            <div>
              <h3>Campaign sequence</h3>
              <div className="sl-sequence compact">
                {hub.contentSequence.map((item) => (
                  <article key={item.day}>
                    <span>Day {item.day}</span>
                    <strong>{item.channel}</strong>
                    <p>{item.copy}</p>
                  </article>
                ))}
              </div>
            </div>
            <div>
              <h3>Source dossier</h3>
              <div className="sl-dossier">
                {hub.sourceDossier.slice(0, 8).map((source) => (
                  <a key={source.sourceId} href={source.url} target="_blank" rel="noreferrer">
                    <span>{source.provider}</span>
                    <strong>{source.title}</strong>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </AppShell>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/campaign/:campaignId" element={<CampaignDetail />} />
        <Route path="/launch/:slug" element={<PublicHub />} />
      </Routes>
    </BrowserRouter>
  );
}
